"use client";

import React from "react";
import { Lock, TrendingUp, BellRing, ChartBar as BarChart3, ArrowRight, ShieldCheck } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function ClientPortalFeatures() {
  const features = [{
    id: "competitor-rates",
    title: "Competitor rate tracking",
    description: "Continuous monitoring of rival property rate movements, seasonal minimum stays, and distribution channel tactics across your primary comp-set.",
    icon: TrendingUp,
    stat: "Live Comp-Set Delta"
  }, {
    id: "parity-alerts",
    title: "Parity and pace alerts",
    description: "Immediate notifications when OTA rate undercutting occurs or when your property's booking pace falls behind historical benchmarks.",
    icon: BellRing,
    stat: "Real-Time Pace Monitoring"
  }, {
    id: "revpar-insights",
    title: "RevPAR yield insights",
    description: "Precision analytics breaking down ADR, occupancy yield curves, and net contribution after commission deductions for data-driven rate decisions.",
    icon: BarChart3,
    stat: "Net RevPAR Optimization"
  }];
  return <section id="clientportalfeatures" data-nav="dark" className="relative dark bg-primary text-primary-foreground py-20 lg:py-28 overflow-hidden border-t border-border/20">
      {/* Subtle background ambient glow */}
      <div className="absolute inset-0 pointer-events-none opacity-20 overflow-hidden" aria-hidden="true">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-secondary/30 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-secondary/20 blur-3xl" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Context, Heading & CTA */}
          <div className="lg:col-span-5 flex flex-col items-start space-y-6">
            <Badge variant="secondary" className="bg-secondary/20 text-secondary border border-secondary/40 px-3 py-1 text-xs tracking-wider uppercase font-semibold">
              <ShieldCheck className="size-3.5 mr-1.5 text-secondary inline-block" />
              Exclusive Client Portal
            </Badge>

            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
              Take control of your rates with <span className="text-secondary">live market intelligence</span>.
            </h2>

            <p className="text-base sm:text-lg text-primary-foreground/80 leading-relaxed max-w-xl">
              Gain direct visibility into dynamic demand signals, distribution health, and rate parity indexes. Our proprietary analytics dashboard empowers hotel owners and general managers with actionable data to maximize yield and outperform competitors.
            </p>

            <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="bg-secondary hover:bg-secondary/90 text-primary font-semibold shadow-md px-7 h-12 transition-transform duration-200 hover:-translate-y-0.5">
                <Link to="#" className="inline-flex items-center gap-2">
                  Request Portal Access
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>

            <div className="flex items-center gap-2 text-xs sm:text-sm text-primary-foreground/70 pt-1">
              <Lock className="size-4 text-secondary shrink-0" />
              <span>Encrypted client-only data access</span>
            </div>
          </div>

          {/* Right Column: Three Feature Cards */}
          <div className="lg:col-span-7 flex flex-col gap-5">
            {features.map((feature, i) => {
            const IconComponent = feature.icon;
            return <Card key={feature.id} data-index={i} className="bg-card text-card-foreground border-border/80 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-secondary/50">
                  <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
                    <div className="flex items-center gap-3.5">
                      <div className="p-2.5 rounded-lg bg-secondary/15 text-secondary border border-secondary/30">
                        <IconComponent className="size-5" />
                      </div>
                      <div>
                        <CardTitle className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
                          {feature.title}
                        </CardTitle>
                        <span className="text-xs font-mono font-medium text-secondary-foreground/70 uppercase tracking-wider block mt-0.5">
                          {feature.stat}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-2 text-muted-foreground text-sm sm:text-base leading-relaxed">
                    <CardDescription className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>;
          })}
          </div>

        </div>
      </div>
    </section>;
}